import type { ClientRect } from "@dnd-kit/core";
import { horizontalListSortingStrategy, type SortingStrategy } from "@dnd-kit/sortable";

import type { TextDirection } from "@/lib/i18n/direction";

/** `rect` seen in a mirror at x = 0: its left edge is where its right edge was. */
function mirrored(rect: ClientRect): ClientRect {
  return { ...rect, left: -rect.right, right: -rect.left };
}

/**
 * dnd-kit's horizontal strategy for a row that runs right to left.
 *
 * The stock strategy assumes item 0 is leftmost: it measures the gap between
 * neighbours as "this item's left minus the previous item's right", which in
 * an Arabic header row comes out as minus both widths, so the headers beside
 * a dragged column slid by the wrong amount, or the wrong way. Seen in a
 * mirror, the row runs left to right again: the stock strategy runs on the
 * mirrored rects, and its answer is mirrored back by negating x.
 */
export const rightToLeftHorizontalListSortingStrategy: SortingStrategy = (args) => {
  const transform = horizontalListSortingStrategy({
    ...args,
    // `rects` can have holes (an item not measured yet); map keeps them.
    rects: args.rects.map(mirrored),
    activeNodeRect: args.activeNodeRect ? mirrored(args.activeNodeRect) : null,
  });
  // 0 - x, not -x: a header that stays put gets 0, never -0.
  return transform ? { ...transform, x: 0 - transform.x } : null;
};

/** The horizontal sorting strategy for a row laid out in `direction`. */
export function horizontalListSortingStrategyFor(direction: TextDirection): SortingStrategy {
  return direction === "rtl" ? rightToLeftHorizontalListSortingStrategy : horizontalListSortingStrategy;
}
